import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { checkPaymentIntent as checkPaymentIntentAction } from '../../../../../../src/web/store/modules/movein';
import styles from './styles.scss';
import SMSearchResultsLoadingIndicator from '../../../../components/stateless/SMSearchResultsLoadingIndicator';
import Translation from '../../../../../components/stateless/Translation';

// CONFIGURABLE VARIABLES:
const source = 'SM Tenant Portal'; // This could be any string you want

// NON-CONFIGURABLE VARIABLES:
const mfeSource = 'MFE.Payments Portal';

const propTypes = {
  checkPaymentIntent: PropTypes.func.isRequired,
  currentFacility: PropTypes.shape().isRequired,
  loadingMessage: PropTypes.string.isRequired,
  mfeSrcUrl: PropTypes.string.isRequired,
  onPaymentIntentSuccess: PropTypes.func.isRequired,
  paymentIntentId: PropTypes.string.isRequired,
  syncMessage: PropTypes.string.isRequired,
};

const defaultProps = {};

const AsyncPaymentHandler = ({
  checkPaymentIntent,
  currentFacility,
  loadingMessage,
  mfeSrcUrl,
  onPaymentIntentSuccess,
  paymentIntentId,
  syncMessage,
}) => {
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState('auto');
  const [showIframe, setShowIframe] = useState(true);
  const [showSyncMessage, setShowSyncMessage] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const pollingIntervalRef = useRef(null);

  // ////////////////////////////////////////////////////////////////////////////////////////////
  //                    IMPORTANT!
  // This is a workaround, ideally Edge should/will return `token` separately in the API response
  // ////////////////////////////////////////////////////////////////////////////////////////////
  const token = new URL(mfeSrcUrl).searchParams.get('token');

  const startPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const pollIntent = async () => {
      try {
        const result = await checkPaymentIntent(currentFacility.id, paymentIntentId);
        if (result.data.paymentIntent.success && result.data.paymentIntent.status === 'succeeded') {
          // Payment intent is succeeded, we can stop polling
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          if (onPaymentIntentSuccess) {
            onPaymentIntentSuccess();
          }
        }
      } catch (error) {
        console.error('Error polling payment intent:', error);
      }
    };

    pollingIntervalRef.current = setInterval(pollIntent, 2000); // Poll every 2 seconds
  };

  const handleEmbeddedEvent = (event) => {
    const message = {
      name: 'storable.mfe.embedacknowledged',
      source: `APP.${source}`,
      destination: mfeSource,
      instanceUuid: event.data.instanceUuid,
      version: '2.0.0',
      payload: {
        origin: window.origin,
      },
    };
    event.source.window.postMessage(message, event.origin);
  };

  // There are a number of different MFE events that can be handled.
  // This type of event is specific to the "Storable Payment MFE".
  // The two that are probably useful are `intent_result` and `intent_processing`,
  // the rest are more for internal use.
  // Events can fire in the following order(s):
  // processing -> result
  // processing -> auth_failed -> processing ...
  // processing -> result -> cancel
  // processing -> result -> retry -> processing ...
  const handleMfeEvents = (event) => {
    switch (event.data.payload.type.toLowerCase()) {
      case 'intent_processing':
        // payment is processing (on processor's end)
        // We can't hide the iframe yet since the payment provider will
        // show the account confirmation screen
        // setShowLoader(true);
        // setShowIframe(false);
        break;
      case 'intent_result':
        // payment is completely done, successfully or otherwise
        setShowLoader(false);

        if (event.data.payload.data.status === 'succeeded') {
          // Hide iframe and show sync message
          setShowIframe(false);
          setShowSyncMessage(true);

          // Start polling for payment intent status
          startPolling();
        } else {
          // Payment failed, show iframe
          setShowIframe(true);
        }
        // note: 'event.data.payload.data.stoPayIntentId' is NOT
        // the same payment intent returned by the Edge api
        break;
      case 'intent_auth_failed':
      case 'intent_cancel':
      case 'intent_retry':
      case 'mfe_ready':
      default:
        console.log('Received unhandled MFE event type:', event.data.payload.type);
    }
  };

  const handleUpdateDisplay = (event) => {
    setIframeHeight(`${event.data.payload.minimumSize.height}px`);
  };

  // The MFE will request an auth token via `storable.mfe.requestauthtoken`
  // and we need to respond with `storable.mfe.applyauthtoken`
  const handleAuthEvent = (event) => {
    const message = {
      name: 'storable.mfe.applyauthtoken',
      source: `APP.${source}`,
      destination: mfeSource,
      instanceUuid: event.data.instanceUuid,
      version: '2.0.0',
      payload: {
        token,
      },
    };
    event.source.window.postMessage(message, event.origin);
  };

  const handleIframeLoad = () => {
    console.log('Iframe loaded!', mfeSrcUrl);
  };

  useEffect(() => {
    if (mfeSrcUrl) {
      console.log('Loading Iframe with mfeSrcUrl:', mfeSrcUrl);
    }
  }, [mfeSrcUrl]);

  useEffect(() => {
    const handler = (event) => {
      // only show events from the expected source
      if (event.data.source !== mfeSource) { return; }

      // We're abstracting pieces from the Storable MFE library,
      // which means the event handling is a little strange here.
      // Essentially, `mfemessage` is a different class of events,
      // which could have a number of different types, so it gets its own handler.
      const eventType = event.data.name.toLowerCase();
      console.log('Received event:', eventType);
      if (eventType === 'storable.mfe.embedded') {
        handleEmbeddedEvent(event);
      } else if (eventType === 'storable.mfe.mfemessage') {
        handleMfeEvents(event);
      } else if (eventType === 'storable.mfe.updatedisplay') {
        handleUpdateDisplay(event);
      } else if (eventType === 'storable.mfe.requestauthtoken') {
        handleAuthEvent(event);
      } else {
        console.log('Received unhandled event type:', eventType, event);
      }
    };

    window.addEventListener('message', handler);

    return () => {
      window.removeEventListener('message', handler);
    };
  }, [handleEmbeddedEvent, handleMfeEvents, handleUpdateDisplay]);

  return (
    <div>
      {mfeSrcUrl ? (
        <>
          <div className="mx-[9%] mt-4">

            <p>
              <Translation
                id="widgets.checkout.supportCCText"
                defaultMessage="Your unit has been rented. Complete payment to gain access to your unit."
              />
            </p>
          </div>
          <iframe
            src={mfeSrcUrl}
            className={styles.iframe}
            ref={iframeRef}
            style={{ height: iframeHeight }}
            hidden={!showIframe}
            title="Async Payment Handler"
            onLoad={handleIframeLoad}
          />
        </>
      ) : null}
      {showLoader || showSyncMessage ? (
        <div className={styles.syncMessage}>
          <div className={styles.syncContent}>
            <div className={styles.syncText}>
              {showLoader ? (
                <>
                  <div><SMSearchResultsLoadingIndicator /></div>
                  <div>
                    {loadingMessage}
                  </div>
                </>
              ) : null}
              {showSyncMessage ? (
                <>
                  <div><SMSearchResultsLoadingIndicator /></div>
                  <div>
                    {syncMessage}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

AsyncPaymentHandler.propTypes = propTypes;
AsyncPaymentHandler.defaultProps = defaultProps;

const mapStateToProps = () => ({});

const mapDispatchToProps = {
  checkPaymentIntent: checkPaymentIntentAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AsyncPaymentHandler);
